import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  CalendarIcon,
  MapPinIcon,
  PlusIcon,
  Settings2Icon,
  XIcon,
  ZapIcon,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  searchCandidateDistanceOptions,
  searchLocationOptions,
  searchYearsOfExperienceOptions,
} from "@/lib/constants";
import { SearchParameters } from "@/types";

const searchSchema = z.object({
  customFilter: z.array(z.string()),
  location: z.string(),
  radius: z.string(),
  yoe: z.string(),
});

const CustomFilterForm = ({
  handleAddCustomFilter,
}: {
  handleAddCustomFilter: (value: string) => void;
}) => {
  const [value, setValue] = useState("");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSubmit = (e: any) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    e.preventDefault();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    e.stopPropagation();
    if (!value) return;
    setValue("");
    handleAddCustomFilter(value);
  };

  return (
    <form className="flex gap-2" onSubmit={handleSubmit}>
      <input
        className="placeholder:text-normal w-full rounded-lg bg-slate-100 px-4 py-2 text-sm text-slate-700 placeholder:text-sm placeholder:text-slate-500 focus:outline-none"
        onChange={(e) => {
          setValue(e.target.value);
        }}
        placeholder="Hit enter or press 'Add' to include in your list"
        value={value}
      />
      <Button className="gap-1 rounded-lg" type="submit" variant="outline">
        <PlusIcon className="h-4 w-4" />
        Add
      </Button>
    </form>
  );
};

export const SearchForm = ({
  searchParameters,
  setSearchParameters,
}: {
  searchParameters: SearchParameters;
  setSearchParameters: (searchParameters: SearchParameters) => void;
}) => {
  const form = useForm({
    resolver: zodResolver(searchSchema),
    defaultValues: {
      customFilter: searchParameters.customFilter ?? [],
      location: searchParameters.location,
      radius: searchParameters.radius,
      yoe: searchParameters.yoe,
    },
  });

  // Reset the form when searchParameters change
  useEffect(() => {
    form.reset({
      ...form.getValues(),
      customFilter: searchParameters.customFilter ?? [],
      location: searchParameters.location,
      radius: searchParameters.radius,
      yoe: searchParameters.yoe,
    });
  }, [searchParameters, form, form.reset]);

  const handleSubmit = (values: z.infer<typeof searchSchema>) => {
    setSearchParameters({
      ...searchParameters,
      ...values,
    });
  };

  const handleAddCustomFilter = (value: string) => {
    form.setValue("customFilter", [...form.getValues().customFilter, value]);
  };

  const handleRemoveCustomFilter = (index: number) => {
    const customFilter = form.getValues().customFilter;
    form.setValue("customFilter", [
      ...customFilter.slice(0, index),
      ...customFilter.slice(index + 1),
    ]);
  };

  const handleClearCustomFilter = () => {
    form.setValue("customFilter", []);
  };

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-4"
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <div className="group flex items-start justify-between rounded-lg border border-slate-200 px-4 transition-all duration-300 ease-out hover:shadow-[0_4px_6px_0_rgba(0,0,0,0.08)]">
          <FormField
            control={form.control}
            name="customFilter"
            render={({ field }) => (
              <FormItem className="w-[75%] space-y-0 border-r border-slate-200 pt-2">
                <FormLabel className="flex items-center gap-1">
                  <ZapIcon className="h-4 w-4 fill-primary-900 stroke-primary-900" />
                  <span className="text-slate-700">
                    Add skills and background
                  </span>
                </FormLabel>
                <FormControl>
                  <Popover>
                    <PopoverTrigger className="min-h-10 w-full cursor-auto py-[7px] text-left">
                      {!field.value.length ? (
                        <span className="text-sm text-slate-500">
                          Search for &quot;Automotive experience&quot;
                        </span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {field.value.map((value, index) => (
                            <Badge
                              className="gap-1 rounded-full bg-white text-xs font-normal text-slate-800"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveCustomFilter(index);
                              }}
                              key={value}
                              variant="outline"
                            >
                              {value}
                              <XIcon className="h-4 w-4" />
                            </Badge>
                          ))}
                        </div>
                      )}
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      alignOffset={-16}
                      className="w-[600px]"
                      sideOffset={8}
                    >
                      <div className="flex flex-col gap-2">
                        <span className="font-semibold text-slate-700">
                          Search for...
                        </span>
                        <div>
                          <CustomFilterForm
                            handleAddCustomFilter={handleAddCustomFilter}
                          />
                        </div>
                        {/* <div className="flex flex-col gap-2 rounded-lg bg-[linear-gradient(180deg,_rgba(201,222,254,0.16)_0%,_rgba(185,206,238,0.16)_47.5%,_rgba(173,204,253,0.16)_100%)] p-4">
                          <div className="flex items-center gap-1">
                            <ZapIcon className="h-4 w-4 fill-primary-900 stroke-primary-900" />
                            <span className="text-sm font-medium text-slate-700">
                              AI Suggestions
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            {idToJobMap[searchParameters.jobId]
                              .aiSearchSuggestions.length && (
                              <>
                                {idToJobMap[
                                  searchParameters.jobId
                                ].aiSearchSuggestions.map((suggestion) => {
                                  if (!field.value.includes(suggestion)) {
                                    return (
                                      <Badge
                                        className="gap-1 rounded-full bg-white text-xs font-normal text-slate-800"
                                        onClick={() => {
                                          handleAddCustomFilter(suggestion);
                                        }}
                                        key={suggestion}
                                        variant="outline"
                                      >
                                        {suggestion}
                                        <PlusIcon className="h-4 w-4" />
                                      </Badge>
                                    );
                                  }
                                  return null;
                                })}
                              </>
                            )}
                          </div>
                        </div> */}
                        <div className="flex w-full justify-end">
                          <Button
                            onClick={handleClearCustomFilter}
                            variant="ghost"
                          >
                            Clear
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex w-[25%] items-center justify-between">
            <FormField
              control={form.control}
              name="location"
              render={({ field }) => {
                const { ref, ...rest } = field;
                return (
                  <FormItem className="space-y-0 pl-4 pt-2">
                    <FormLabel className="flex items-center gap-1">
                      <MapPinIcon className="h-4 w-4 text-slate-700" />
                      <span className="text-slate-700">Location</span>
                    </FormLabel>
                    <Select
                      defaultValue={field.value}
                      disabled={!!searchParameters.jobId}
                      onValueChange={field.onChange}
                      {...rest} // TODO: Why do we need to pass this?
                    >
                      <FormControl className="!h-10 w-auto p-0">
                        <SelectTrigger className="border-none">
                          <SelectValue placeholder="Select a location" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {searchLocationOptions.map((location) => (
                          <SelectItem
                            key={location.value}
                            value={location.value}
                          >
                            {location.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <Button
              className="gap-0 rounded-full transition-all duration-300 ease-out group-hover:border-none group-hover:bg-primary-900 group-hover:text-white group-hover:shadow-[0_4px_6px_0px_rgba(0,0,0,0.09)]"
              type="submit"
              variant="outlineSecondary"
            >
              Refine
              <Settings2Icon className="h-4 !w-0 transition-all duration-300 ease-out group-hover:ml-2 group-hover:!w-4" />
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <FormField
            control={form.control}
            name="yoe"
            render={({ field }) => {
              const { ref, ...rest } = field;
              return (
                <FormItem className="text-slate-700">
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    {...rest} // TODO: Why do we need to pass this?
                  >
                    <FormControl className="flex h-auto gap-[6px] border border-primary-900 bg-primary-400 px-[10px] py-1">
                      <SelectTrigger className="text-slate-700 [&>svg]:opacity-100">
                        <CalendarIcon className="h-4 w-4" />
                        <SelectValue placeholder="Select years of experience" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="text-slate-700">
                      {searchYearsOfExperienceOptions.map(
                        (yearsOfExperienceOption) => (
                          <SelectItem
                            key={yearsOfExperienceOption.value}
                            value={yearsOfExperienceOption.value}
                          >
                            {yearsOfExperienceOption.label}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            name="radius"
            render={({ field }) => {
              const { ref, ...rest } = field;
              return (
                <FormItem className="text-slate-700">
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    {...rest} // TODO: Why do we need to pass this?
                  >
                    <FormControl className="flex h-auto gap-[6px] border border-primary-900 bg-primary-400 px-[10px] py-1">
                      <SelectTrigger className="text-slate-700 [&>svg]:opacity-100">
                        <MapPinIcon className="h-4 w-4" />
                        <SelectValue placeholder="Select candidate distance" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="text-slate-700">
                      {searchCandidateDistanceOptions.map(
                        (candidateDistanceOption) => (
                          <SelectItem
                            key={candidateDistanceOption.value}
                            value={candidateDistanceOption.value}
                          >
                            {candidateDistanceOption.label}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>
      </form>
    </Form>
  );
};
